import { Component, ElementRef, ViewChild } from '@angular/core';
import { OrganisationUnitService } from '../../../services/organisation-unit.service';
import { tap, concatMap } from 'rxjs';
import { OrganisationUnitModel } from '../../../models/organisation-unit/organisation-unit.model';
import { OrganisationUnitCreationModel } from '../../../models/organisation-unit/organisation-unit-creation.model';
import { OrganisationUnitUpdateModel } from '../../../models/organisation-unit/organisation-unit-update.model';
import { ActivatedRoute, Router } from '@angular/router';
import { LoadingService } from '../../../services/loading.service';
import { DxSelectBoxTypes } from 'devextreme-angular/ui/select-box';
import { DxFormComponent } from 'devextreme-angular/ui/form';
import { FileUtilService } from '../../../services/file-util.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import validationEngine from 'devextreme/ui/validation_engine';
import { DxDropDownBoxComponent, DxHtmlEditorComponent } from 'devextreme-angular';
import { PersonSummaryModel } from 'src/app/models/person/person-summary.model';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { DxTabsTypes } from 'devextreme-angular/ui/tabs';
import { TableColumn } from 'src/app/models/table-column.model';
import { PageStateService } from 'src/app/services/page-state.service';
import { PageStateFlags } from 'src/app/constants/page-state.constants';

@Component({
  selector: 'app-organization-unit-form',
  templateUrl: './organization-unit-form.component.html',
  styleUrls: ['./organization-unit-form.component.scss'],
  standalone: false
})
export class OrganizationUnitFormComponent {
  @ViewChild('form', { static: true }) form!: DxFormComponent;
  @ViewChild('bottomForm', { static: true }) bottomForm!: DxFormComponent;
  @ViewChild(DxDropDownBoxComponent, { static: false }) parentUnitDropDown!: DxDropDownBoxComponent;

  editingOrganisationUnitId: string | null;
  editingOrganisationUnit: OrganisationUnitModel | null = null;
  organisationUnits: OrganisationUnitModel[] = [];
  organisationUnitForm:
    | OrganisationUnitCreationModel
    | OrganisationUnitUpdateModel = {
    name: '',
    description: '',
    parentId: '',
    reportFooter: '',
    type: null,
    image: null,
  };
  imageBlob: Blob | null = null;

  optionTabs: any[] = [
    { 
      id: 0,
      text: this.translateService.instant('COMMON.FIELDS.LEGAL-FOOTER'),
      key: 'legalFooter',
    },
    {
      id: 1,
      text: this.translateService.instant('SIDENAV.EMPLOYEES'),
      key: 'employees',
    },
  ];

  selectedTab$ = new BehaviorSubject<'legalFooter' | 'employees'>(
    this.optionTabs[0].key
  );

  displayPersons$!: Observable<PersonSummaryModel[]>;
  personsColumns: TableColumn[] = [
    {
      dataField: 'lastName',
      caption: this.translateService.instant('COMMON.FIELDS.LAST-NAME'),
      cssClass: 'width-200',
    },
    {
      dataField: 'firstName',
      caption: this.translateService.instant('COMMON.FIELDS.FIRST-NAME'),
      cssClass: 'width-200',
    },
    {
      dataField: 'email',
      caption: this.translateService.instant('COMMON.FIELDS.EMAIL'),
      cssClass: 'width-150',
    },
    {
      dataField: 'location.name',
      caption: this.translateService.instant('COMMON.FIELDS.LOCATION'),
      cssClass: 'width-150',
    },
  ];

  typeOptions!: DxSelectBoxTypes.Properties;

  constructor(
    private organisationUnitService: OrganisationUnitService,
    private route: ActivatedRoute,
    private router: Router,
    private loadingService: LoadingService,
    private fileUtilService: FileUtilService,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private pageStateService: PageStateService
  ) {
    const parentId: string | null = this.route.snapshot.queryParams['parent'];
    this.editingOrganisationUnitId = this.route.snapshot.params['id'];

    if (this.editingOrganisationUnitId) {
      const result = this.loadingService.load(
        this.organisationUnitService.getAllOrganisationUnits()
      ).pipe(
        tap(
          (organisationUnits) => {
            this.organisationUnits = organisationUnits.filter(ou => ou.id !== this.editingOrganisationUnitId);
            //excluded editing organisation unit
          }
        ),
        concatMap(() =>
          this.organisationUnitService.getOrganisationUnit(
            this.editingOrganisationUnitId!
          )
        )
      );

      result.subscribe((organisationUnit) => {
        this.editingOrganisationUnit = organisationUnit;

        if (this.editingOrganisationUnit.type == 1) {
          this.organisationUnits = this.organisationUnits.filter((ou: any) => ou.type == 1);
        }
        this.setDropdownOptions();
        this.patchForm(organisationUnit);

        this.displayPersons$ = this.loadingService
          .load(
            this.organisationUnitService.getOrganizationUnitPersons(this.editingOrganisationUnitId ?? '')
          );

      });
    } else {
      this.loadingService.load(
        this.organisationUnitService.getAllOrganisationUnits()
      ).pipe(
          tap(
            (organisationUnits) => (this.organisationUnits = organisationUnits)
          )
        )
        .subscribe(() => {
          this.organisationUnitForm.parentId = parentId!;
          this.setDropdownOptions();
        });
    }
  }

  onTabChange(e: DxTabsTypes.ItemClickEvent) {
    this.selectedTab$.next(e.itemData.key);
  }

  navigateBack(): void {
    this.pageStateService.setFlag(
      PageStateFlags.RestoreOrganisationUnitsOverview
    );

    this.router.navigate(['/organisation-unit']);
  }

  onFormSubmit(): void {
    const isFormValid = this.form.instance.validate().isValid;
    const isCustomFieldsValid = validationEngine.validateGroup('customFields').isValid;
    
    if (isFormValid) {
      if (this.editingOrganisationUnitId) {
        const organizationUnit = {
          ...this.organisationUnitForm,
          image: this.getImage(),
        };

        const result = this.organisationUnitService.updateOrganisationUnit(
          this.editingOrganisationUnitId!,
          organizationUnit
        );
        this.loadingService.load(result).subscribe({
          next: (updatedOrganisationUnit) => {
            this.toastrService.success(
              updatedOrganisationUnit.name +
                this.translateService.instant('COMMON.UPDATED-SUCCESSFULLY')
            );
            window.location.href = '/organisation-unit';
          },
          error: () =>
            this.toastrService.error(
              this.translateService.instant('COMMON.UNKNOWN-ERROR')
            ),
        });
      } else {
        const organizationUnit = {
          ...this.organisationUnitForm,
          image: this.getImage(),
        } as OrganisationUnitCreationModel;

        const result =
          this.organisationUnitService.addOrganisationUnit(organizationUnit);
        this.loadingService.load(result).subscribe({
          next: (createdOrganisationUnit) => {
            this.toastrService.success(
              createdOrganisationUnit.name +
                this.translateService.instant('COMMON.CREATED-SUCCESSFULLY')
            );
            this.router.navigateByUrl('organisation-unit');
          },
          error: () =>
            this.toastrService.error(
              this.translateService.instant('COMMON.UNKNOWN-ERROR')
            ),
        });
      }
    }
  }

  setDropdownOptions() {
    const defaultSelectOptions = {
      showClearButton: true,
      stylingMode: 'outlined',
      displayExpr: 'name',
      valueExpr: 'id',
      disabled: this.editingOrganisationUnitId ? true : false
    } as DxSelectBoxTypes.Properties;

    const parentOrganizationUnitType = this.organisationUnits.find(ou => ou.id === this.organisationUnitForm.parentId)?.type;
    const types = [{
      id: 2,
      name: this.translateService.instant('COMMON.FIELDS.DEPARTMENT'),
    }];

    if (parentOrganizationUnitType == 1 || this.editingOrganisationUnitId) {
      types.push({
        id: 1,
        name: this.translateService.instant('COMMON.FIELDS.LOCATION'),
      });
    }

    this.typeOptions = {
      ...defaultSelectOptions,
      dataSource: types,
      placeholder: '',
    };
  }

  onOrganisationUnitSelected(e: any) {
    if (e.node.key) {
      this.organisationUnitForm.parentId = e.node.key;
      this.setDropdownOptions();
    }
  }

  onImageUploaded(files: any) {
    const file = files[0];
    this.imageBlob = new Blob([file], { type: file.type });
    this.organisationUnitForm.image = URL.createObjectURL(this.imageBlob);
  }

  getImage(): File | null {
    if (this.imageBlob) {
      return new File([this.imageBlob!], 'defaultname');
    } else return null;
  }

  private patchForm(organisationUnit: OrganisationUnitModel) {
    this.organisationUnitForm.name = organisationUnit.name;
    this.organisationUnitForm.description = organisationUnit.description;
    this.organisationUnitForm.parentId = organisationUnit.parentId!;
    this.organisationUnitForm.reportFooter = organisationUnit.reportFooter;
    this.organisationUnitForm.type = organisationUnit.type!;

    const imageUrl = organisationUnit.imageFile?.fileUrl;
    if (imageUrl) {
      this.loadingService
        .load(this.fileUtilService.imageUrlToBlob(imageUrl))
        .subscribe((blob) => {
          this.imageBlob = blob;
          this.organisationUnitForm.image = URL.createObjectURL(blob);
        });
    }
  }

  onParentUnitsSelected(e: any) {
    if (e.node.key) {
      this.organisationUnitForm.parentId = e.node.key;
      this.setDropdownOptions();
    }
  }
}
